import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import {
  InputNumber,
  InputNumberChangeEvent,
  InputNumberValueChangeEvent,
} from "primereact/inputnumber";
import { SelectButton, SelectButtonChangeEvent } from "primereact/selectbutton";
import { Skeleton } from "primereact/skeleton";

import ErrorPage from "../../../../../../Components/ErrorPage";
import NoDataCreated from "../../../../../../Components/NoDataCreated";
import PageLoader from "../../../../../../Components/PageLoader";
import { goToTop } from "../../../../../../Components/goToTop";
import { getAppCurrencyType } from "../../../../../../Utils/Common";

import { GDVCost } from "../../../../../../Controllers/Types/Project/DefaultModels/DevelopmentAppraisal/GDVCost";
import {
  addGdvCost,
  deleteGDVCostData,
  updateGdvCost,
} from "../../../../../../Api/Project/DefaultServices/DevelopmentAppraisalService/DevelopmentAppraisalApiService";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../Controllers/hooks";
import { getAllDevelopmentAppraisalList } from "../../../../../../Controllers/Thunks/DevelopmentAppraisalThunks";
import { DevelopmentAppraisalDataList } from "../../../../../../Controllers/Types/Project/DefaultModels/DevelopmentAppraisal/DevelopmentAppraisalDataList";
import { checkNumber, checkText } from "../../../../../../Utils/Validator";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../Controllers/Store";
import { hasPermission } from "../../../../../../Controllers/Selectors/permissionsSelector";
import TableActionLoader from "../../../../../../Components/TableActionLoader";

const GDVCostDetail: React.FC = () => {
  const module = "GDV Cost";
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const currencySymbol = getAppCurrencyType();
  const toast = useRef<Toast>(null);
  const isShared: boolean = location.state?.shared_by !== null;
  const canEdit: boolean = Boolean(location.state?.canEdit);

  const loadingPage = useAppSelector((state) => state?.developmetAppraisalList?.loader);
  const error = useAppSelector((state) => state?.developmetAppraisalList?.errorDisplay);

  const [loading, setLoading] = useState<boolean>(false);

  const [contentLoading, setContentLoading] = useState<boolean>(false);
  const [dataState, setDataState] = useState<"Add" | "Edit">("Edit");

  const [folderData, setFolderData] = useState<any | null>(null);
  const [gdvData, setGDVCost] = useState<GDVCost[] | null>([]);

  const [selectedGDVData, setSelectedGDVData] = useState<GDVCost | null>(gdvData && gdvData.length > 0 ? gdvData[0] : null);
  const [showGDVModal, setShowGDVModal] = useState<boolean>(false);

  const viewSection = useRef<HTMLElement | null>(null);
  const options: string[] = ["Yes", "No"];
  const sqmBySqft = 10.76;
  const sqftBySqm = 0.09;

  const [flatName, setFlatName] = useState<string>("");
  const [areaSqm, setAreaSqm] = useState<number>(0);
  const [areaSqft, setAreaSqft] = useState<number>(0);
  const [pricePerSqm, setPricePerSqm] = useState<number>(0);
  const [pricePerSqft, setPricePerSqft] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [numberOfBedroom, setNumberOfBedroom] = useState<number>(0);
  const [numberOfBathroom, setNumberOfBathroom] = useState<number>(0);
  const [numberOfShowerroom, setNumberOfShowerroom] = useState<number>(0);
  const [garden, setGarden] = useState<string>("No");
  const [balcony, setBalcony] = useState<string>("No");
  const [flatLocation, setFlatLocation] = useState<string>("");

  const [folderId, setFolderId] = useState<number>(0);
  const [appraisalData, setAppraisalData] = useState<DevelopmentAppraisalDataList | null>(null);

  const [gdvCostUpdateHasChanges, setGDVCostUpdateHasChanges] = useState<boolean>(false);
  const [deleteGDVCostId, setDeleteGDVCostId] = useState<number | null>(null);

  //Error message
  const [flatNameError, setFlatNameError] = useState<string>("");
  const [areaSqmEr, setAreaSqmEr] = useState<string>("");
  const [areaSqftEr, setAreaSqftEr] = useState<string>("");
  const [pricePerSqmEr, setPricePerSqmEr] = useState<string>("");
  const [pricePerSqftEr, setPricePerSqftEr] = useState<string>("");

  // PERMISION HANDLER
  const useCanEdit = (screen: string, permission: string) => {
    return useSelector((state: RootState) =>
      hasPermission(state, screen, permission)
    );
  };

  const hasPermissionForAddCost = (useCanEdit("Development appraisal", "add_details_cost"));
  const hasPermissionForEditCost = useCanEdit("Development appraisal", "edit_details_cost");
  const hasPermissionForDeleteCost = useCanEdit("Development appraisal", "delete_details_cost");

  const checkAccessPermission = useCallback(
    (permission: boolean): boolean => {
      return isShared ? canEdit : permission;
    },
    [isShared, canEdit]
  );

  useEffect(() => {
    if (location.state && location.state.folderId) {
      const folder_id = location.state.folderId as number;
      setFolderId(folder_id);
    }

    if (location.state && location.state.appraisalData) {
      const appriasalData = location.state.appraisalData as DevelopmentAppraisalDataList;
      setAppraisalData(appriasalData);
    }

    if (location.state && location.state.folder_data) {
      const folder = location.state.folder_data as any;
      setFolderData(folder);
    }
  }, [location.state]);

  // for fetching data fro api tigger with state object actions
  useEffect(() => {
    developmentAppraisalDataGet();
  }, [folderId]);

  const developmentAppraisalDataGet = async () => {
    if (folderId) {
      await dispatch(getAllDevelopmentAppraisalList(folderId));
    }
  }

  // set gdv values
  // this is get data from state list and set projectlist
  const developmentAppraisalDataList = useAppSelector((state) => state.developmetAppraisalList.developmentAppraisalList);

  //   initially set values both
  useEffect(() => {
    if (developmentAppraisalDataList && developmentAppraisalDataList.length > 0) {
      const gdvDataFilter = developmentAppraisalDataList.filter(
        (e) => e.id === appraisalData?.id
      );
      if (gdvDataFilter && gdvDataFilter.length > 0) {
        setGDVCost(gdvDataFilter[0].development_appraisal_gdv as GDVCost[]);
      }
    }
  }, [developmentAppraisalDataList]);

  // set gdv data as well.
  useEffect(() => {
    if (gdvData && gdvData.length > 0) {
      setSelectedGDVData(gdvData[0]);
    }
  }, [gdvData]);

  const handleViewGDVDetails = (data: GDVCost) => {
    setContentLoading(true);
    setSelectedGDVData(data);
    setTimeout(() => {
      setContentLoading(false);
    }, 500);

    // viewSection.current?.scrollIntoView({ behavior: 'smooth' });

    if (viewSection.current) {
      const elementPosition =
        viewSection.current.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - 100;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleCloseGDVModal = () => {
    setShowGDVModal(false);
    handleClearFields();
  };

  const handleSqFeetChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setAreaSqft(e.value);
      setAreaSqm(e.value * sqftBySqm);
    } else {
      setAreaSqft(0);
      setAreaSqm(0);
    }
  };

  const handleSqMeterChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setAreaSqm(e.value);
      setAreaSqft(e.value * sqmBySqft);
    } else {
      setAreaSqm(0);
      setAreaSqft(0);
    }
  };

  const handleSqMeterPriceChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setPricePerSqm(e.value);
      setTotalPrice(e.value * areaSqm);
      setPricePerSqft(e.value / sqmBySqft);
    } else {
      setPricePerSqm(0);
      setTotalPrice(0);
      setPricePerSqft(0);
    }
  };

  const handleSqFeetPriceChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setPricePerSqft(e.value);
      setTotalPrice(e.value * areaSqft);
      setPricePerSqm(e.value * sqmBySqft);
    } else {
      setPricePerSqft(0);
      setTotalPrice(0);
      setPricePerSqm(0);
    }
  };

  const handleClearFields = () => {
    setFlatName("");
    setAreaSqm(0);
    setAreaSqft(0);
    setPricePerSqm(0);
    setPricePerSqft(0);
    setTotalPrice(0);
    setNumberOfBedroom(0);
    setNumberOfBathroom(0);
    setNumberOfShowerroom(0);
    setGarden("No");
    setBalcony("No");
    setFlatLocation("");

    // ErrorEmty
    setFlatNameError("");
    setAreaSqmEr("");
    setAreaSqftEr("");
    setPricePerSqmEr("");
    setPricePerSqftEr("");
  };

  const handleAddNewFlat = () => {
    setDataState("Add");
    setShowGDVModal(true);
  };

  useEffect(() => {
    checkText(flatName, false, setFlatNameError);
  }, [flatName]);

  useEffect(() => {
    checkNumber(areaSqm!, false, setAreaSqmEr);
  }, [areaSqm]);

  useEffect(() => {
    checkNumber(areaSqft!, false, setAreaSqftEr);
  }, [areaSqft]);

  useEffect(() => {
    checkNumber(pricePerSqm!, false, setPricePerSqmEr);
  }, [pricePerSqm]);

  useEffect(() => {
    checkNumber(pricePerSqft!, false, setPricePerSqftEr);
  }, [pricePerSqft]);

  function checkCredentials(): boolean {
    const titleError = checkText(flatName, true, setFlatNameError);
    const areaSqmEr = checkNumber(areaSqm!, true, setAreaSqmEr);
    const areaSqftEr = checkNumber(areaSqft!, true, setAreaSqftEr);
    const pricePerSqmEr = checkNumber(pricePerSqm!, true, setPricePerSqmEr);
    const pricePerSqftEr = checkNumber(pricePerSqft!, true, setPricePerSqftEr);
    return (
      titleError && areaSqmEr && areaSqftEr && pricePerSqmEr && pricePerSqftEr
    );
  }

  //   databse insert function called....
  const handleCreateGDVData = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentials()) {
      setLoading(true);
      const data = await addGdvCost(
        appraisalData?.id!,
        flatName,
        areaSqm,
        areaSqft,
        pricePerSqm,
        pricePerSqft,
        totalPrice,
        numberOfBedroom,
        numberOfBathroom,
        numberOfShowerroom,
        garden,
        balcony,
        flatLocation
      );

      setLoading(false);
      if (data.statusCode === 200) {
        // call shop dispach event
        developmentAppraisalDataGet();
        handleCloseGDVModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: 'GDV cost details added successfully.',
            life: 3000,
          });
        }
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error creating the GDV cost detail.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleEditGDVData = (data: GDVCost) => {
    setDataState("Edit");
    setShowGDVModal(true);

    setFlatName(data?.title);
    setAreaSqm(data?.area_aqm);
    setAreaSqft(data?.area_sqft);
    setPricePerSqm(data?.price_per_sqm);
    setPricePerSqft(data?.price_per_sqft);
    setTotalPrice(data?.total_price);
    setNumberOfBedroom(data?.no_of_bedroom);
    setNumberOfBathroom(data?.no_of_bath);
    setNumberOfShowerroom(data?.no_of_showrroom);
    setGarden(data?.garden);
    setBalcony(data?.balcony);
    setFlatLocation(data?.location);
  };

  useEffect(() => {
    if (selectedGDVData) {
      if (flatName !== selectedGDVData?.title) {
        setGDVCostUpdateHasChanges(true);
      } else if (areaSqm !== null && areaSqm !== selectedGDVData?.area_aqm) {
        setGDVCostUpdateHasChanges(true);
      } else if (areaSqft !== null && areaSqft !== selectedGDVData?.area_sqft) {
        setGDVCostUpdateHasChanges(true);
      } else if (pricePerSqm !== null && pricePerSqm !== selectedGDVData?.price_per_sqm) {
        setGDVCostUpdateHasChanges(true);
      } else if (pricePerSqft !== null && pricePerSqft !== selectedGDVData?.price_per_sqft) {
        setGDVCostUpdateHasChanges(true);
      } else if (totalPrice !== null && totalPrice !== selectedGDVData?.total_price) {
        setGDVCostUpdateHasChanges(true);
      } else if (numberOfBedroom !== null && numberOfBedroom !== selectedGDVData?.no_of_bedroom) {
        setGDVCostUpdateHasChanges(true);
      } else if (numberOfBathroom !== null && numberOfBathroom !== selectedGDVData?.no_of_bath) {
        setGDVCostUpdateHasChanges(true);
      } else if (numberOfShowerroom !== null && numberOfShowerroom !== selectedGDVData?.no_of_showrroom) {
        setGDVCostUpdateHasChanges(true);
      } else if (garden && garden !== selectedGDVData?.garden) {
        setGDVCostUpdateHasChanges(true);
      } else if (balcony && balcony !== selectedGDVData?.balcony) {
        setGDVCostUpdateHasChanges(true);
      } else if (flatLocation !== selectedGDVData?.location) {
        setGDVCostUpdateHasChanges(true);
      } else {
        setGDVCostUpdateHasChanges(false);
      }
    }
  }, [
    selectedGDVData,
    flatName,
    areaSqm,
    areaSqft,
    pricePerSqm,
    pricePerSqft,
    totalPrice,
    numberOfBedroom,
    numberOfBathroom,
    numberOfShowerroom,
    garden,
    balcony,
    flatLocation
  ]);

  const handleUpdateGDVData = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentials()) {
      setLoading(true);
      const data = await updateGdvCost(
        appraisalData?.id!,
        flatName,
        areaSqm,
        areaSqft,
        pricePerSqm,
        pricePerSqft,
        totalPrice,
        numberOfBedroom,
        numberOfBathroom,
        numberOfShowerroom,
        garden,
        balcony,
        flatLocation,
        selectedGDVData?.id!
      );
      setLoading(false);
      if (data.statusCode === 200) {
        // call shop dispach event
        developmentAppraisalDataGet();
        handleCloseGDVModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: `GDV ${(selectedGDVData?.title)?.toLowerCase()} detail updated successfully.`,
            life: 3000,
          });
        }
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: `Failed`,
            detail: `There was an error updating the GDV ${(selectedGDVData?.title)?.toLowerCase()} detail.`,
            life: 3000,
          });
        }
      }
    }
  };

  const handleDeleteGDVCost = () => {
    confirmDialog({
      message: `Are you sure you want to delete the GDV ${(selectedGDVData?.title)?.toLowerCase()} detail?`,
      header: "Confirm the deletion",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: () => deleteGDVCost(selectedGDVData?.id || 0),
      dismissableMask: true,
      draggable: false,
      resizable: false,
    });
  };

  const deleteGDVCost = async (id: number) => {
    setDeleteGDVCostId(id);
    const result = await deleteGDVCostData(id);

    setDeleteGDVCostId(null);
    if (result.statusCode === 200) {
      developmentAppraisalDataGet();
      handleCloseGDVModal();

      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: 'GDV cost data deleted successfully.',
          life: 3000,
        });
      }
    } else {
      setLoading(false);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "There was an error deleting the GDV cost data.",
          life: 3000,
        });
      }
    }
  };

  const gdvCostModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">
        {dataState === "Add"
          ? "Add GDV detail"
          : `Edit GDV ${selectedGDVData?.title} detail`}
      </h5>

      {dataState === "Edit" && checkAccessPermission(hasPermissionForDeleteCost) ? (
        <React.Fragment>
          {deleteGDVCostId === selectedGDVData?.id ? (
            <TableActionLoader className="is_modal_loader" />
          ) : (
            <button
              type="button"
              className="close_modal_btn p-ripple"
              onClick={handleDeleteGDVCost}
            >
              <i className="bi bi-trash3"></i>
              <Ripple />
            </button>
          )}
        </React.Fragment>
      ) : (
        <button
          type="button"
          className="close_modal_btn p-ripple"
          onClick={handleCloseGDVModal}
        >
          <i className="bi bi-x-circle"></i>
          <Ripple />
        </button>
      )}
    </div>
  );

  const gdvCostModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={handleCloseGDVModal}
      />
      <Button
        label={loading ? "Processing" : dataState === "Add" ? "Save" : "Update"}
        onClick={dataState === "Add" ? handleCreateGDVData : handleUpdateGDVData}
        loading={loading}
        className="custom_btn primary"
        disabled={dataState === 'Edit' && !gdvCostUpdateHasChanges}
      />
    </div>
  );

  /* Common function */
  const formatAmount = (amount: number): string => {
    return amount.toFixed(2);
  };

  const goBack = () => {
    navigate(-1);
    goToTop();
  };
  /*  */

  return (
    <React.Fragment>
      <Toast ref={toast} />
      <Tooltip target={`.tootip_display`} />

      <div className="main_content layout_padding show_side_bar">
        {loadingPage ? (
          <PageLoader />
        ) : error ? (
          <ErrorPage errorMessage={error} />
        ) : (
          <div className="page_content mt-0">
            <div className="page_breadcrumb_area">
              <ul className="page_breadcrumb">
                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                  {folderData?.job_number} : {folderData?.address}
                </li>
                <li className="page_breadcrumb_item has_child">
                  {folderData?.folder}
                </li>
                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                  {folderData?.feature_name}
                </li>
                <li className="page_breadcrumb_item active">
                  {module}
                </li>
              </ul>

              <button className="back_btn p-ripple" onClick={goBack}>
                <i className="bi bi-arrow-left me-2"></i>
                Back
                <Ripple />
              </button>
            </div>

            {/* {gdvData && gdvData?.length === 0 && ( */}
            <div className="page_header_area mt-sm-3 mt-0">
              <div></div>
              <div className="page_action_area">
                {checkAccessPermission(hasPermissionForAddCost) && (
                  <button
                    className="add_data_link_btn p-ripple"
                    onClick={handleAddNewFlat}
                  >
                    <i className="bi bi-plus-circle"></i>
                    <span>Add new flat</span>
                    <Ripple />
                  </button>
                )}
              </div>
            </div>
            {/* )} */}

            <div className="page_main_content_section">
              {gdvData && gdvData?.length > 0 ? (
                <div className="cost_detail_section">
                  <div className="cost_detail_container">
                    <h6 className="cost_detail_head">GDV cost detail</h6>
                    <div className="row mt-3">
                      <div className="col-12 pb-3 d-sm-none ">
                        {checkAccessPermission(hasPermissionForAddCost) && (
                          <button
                            type="button"
                            className={`add_data_btn p-ripple`}
                            onClick={handleAddNewFlat}
                          >
                            <i className={`bi bi-plus-circle`}></i>
                            <span>Add new flat</span>
                            <Ripple />
                          </button>
                        )}
                      </div>

                      <div className="col-12 col-xxl-7 col-xl-7 col-lg-5 col-md-6 col-sm-5 pe-0">
                        <div className="row">
                          <div className="col-12 col-xxl-7 col-xl-10">
                            <div className="flat_data_section">
                              {gdvData?.map((gdv) => {
                                return (
                                  <article
                                    key={gdv?.id}
                                    className={`flat_data_container ${gdv?.id === selectedGDVData?.id && "active"} p-ripple`}
                                    onClick={() => handleViewGDVDetails(gdv)}
                                  >
                                    <h6>{gdv?.title}</h6>
                                    <div className="flat_data_footer">
                                      <p>{gdv?.area_aqm}sqm</p>
                                      <span>|</span>
                                      <div
                                        className="icon_img_grp tootip_display"
                                        data-pr-tooltip="Bedroom"
                                        data-pr-position="top"
                                        data-pr-classname="custom_tooltip"
                                      >
                                        <p>{gdv?.no_of_bedroom}</p>
                                        <img
                                          src="/assets/images/flat/bed_room.svg"
                                          alt="Bedroom"
                                        />
                                      </div>
                                      <span>|</span>
                                      <div
                                        className="icon_img_grp tootip_display"
                                        data-pr-tooltip="Bathroom"
                                        data-pr-position="top"
                                        data-pr-classname="custom_tooltip"
                                      >
                                        <p>{gdv?.no_of_bath}</p>
                                        <img
                                          src="/assets/images/flat/bath_room.svg"
                                          alt="Bathroom"
                                        />
                                      </div>
                                      <span>|</span>
                                      <div
                                        className="icon_img_grp tootip_display"
                                        data-pr-tooltip="Showerroom"
                                        data-pr-position="top"
                                        data-pr-classname="custom_tooltip"
                                      >
                                        <p>{gdv?.no_of_showrroom}</p>
                                        <img
                                          src="/assets/images/flat/shower_room.svg"
                                          alt="Showerroom"
                                        />
                                      </div>
                                    </div>
                                    <Ripple />
                                  </article>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-xxl-5 col-xl-5 col-lg-7 col-md-6 mt-2 col-sm-7 mt-sm-0">
                        {selectedGDVData && selectedGDVData !== null && (
                          <article
                            className="cost_view_container"
                            ref={viewSection}
                          >
                            <div className="cost_view_header">
                              {checkAccessPermission(hasPermissionForEditCost) && (
                                <button
                                  className="data_action_btn cost_edit_btn float p-ripple"
                                  onClick={() => handleEditGDVData(selectedGDVData)}
                                >
                                  <i className="bi bi-pencil-square"></i>
                                  <Ripple />
                                </button>
                              )}

                              <div className="cost_total_area">
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    width="8rem"
                                    className="mb-2 blended"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_detail_head text-white mb-2">
                                    {selectedGDVData?.title} details
                                  </h6>
                                )}
                                <h5>Total cost</h5>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1.4rem"
                                    width="6rem"
                                    className="blended"
                                  ></Skeleton>
                                ) : (
                                  <h5>
                                    {currencySymbol}&nbsp;
                                    {formatAmount(selectedGDVData?.total_price)}
                                  </h5>
                                )}
                              </div>
                            </div>
                            <div className="cost_view_body">
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">Area (sqm)</h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {selectedGDVData?.area_aqm}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">Area (sqft)</h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {selectedGDVData?.area_sqft}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">
                                  Price per sqm
                                </h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {currencySymbol}&nbsp;
                                    {formatAmount(
                                      selectedGDVData?.price_per_sqm
                                    )}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">
                                  Price per sqft
                                </h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {currencySymbol}&nbsp;
                                    {formatAmount(
                                      selectedGDVData?.price_per_sqft
                                    )}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">Total price</h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {currencySymbol}&nbsp;
                                    {formatAmount(selectedGDVData?.total_price)}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">
                                  No. of Bedroom(s)
                                </h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {selectedGDVData?.no_of_bedroom}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">
                                  No. of Bathroom(s)
                                </h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {selectedGDVData?.no_of_bath}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">
                                  No. of Showerroom(s)
                                </h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {selectedGDVData?.no_of_showrroom}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">Garden</h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {selectedGDVData?.garden}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">Balcony</h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {selectedGDVData?.balcony}
                                  </h6>
                                )}
                              </div>
                              <div className="cost_view_data_area">
                                <h6 className="cost_view_title">Location</h6>
                                {contentLoading ? (
                                  <Skeleton
                                    height="1rem"
                                    className="responsived"
                                    width="6rem"
                                  ></Skeleton>
                                ) : (
                                  <h6 className="cost_view_value">
                                    {selectedGDVData?.location}
                                  </h6>
                                )}
                              </div>
                            </div>
                          </article>
                        )}
                      </div>

                      <div className="col-12 col-sm-6 pt-2 d-none d-sm-block add_flat_bottom_btn_area">
                        {checkAccessPermission(hasPermissionForAddCost) && (
                          <button
                            type="button"
                            className={`add_data_btn p-ripple`}
                            onClick={handleAddNewFlat}
                          >
                            <i className={`bi bi-plus-circle`}></i>
                            <span>Add new flat</span>
                            <Ripple />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <NoDataCreated message={`No flat(s) added yet. ${checkAccessPermission(hasPermissionForAddCost) ? 'To add the flat click on add button.' : ''}`} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* GDV Cost modal */}
      <Dialog
        visible={showGDVModal}
        header={gdvCostModalHeader}
        footer={gdvCostModalFooter}
        headerClassName="custom_modal_header"
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseGDVModal}
        dismissableMask
      >
        <div className="custom_modal_body">
          <div className="add_cost_section p-0">
            <div className="row mb-sm-2 mb-1">
              <div className="col-4 pe-sm-2 pe-1">
                <label
                  htmlFor="flatName"
                  className="cost_title custom is_required"
                >
                  Title
                </label>
              </div>
              <div className="col-8 ps-sm-2 ps-1">
                <InputText
                  id="flatName"
                  value={flatName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFlatName(e.target.value)}
                  className="cost_input custom text_input w-100"
                  placeholder="Enter title"
                />

                {flatNameError && (
                  <small className="form_error_msg">{flatNameError}</small>
                )}
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label
                  htmlFor="areaSqm"
                  className="cost_title custom is_required"
                >
                  Area (sqm)
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <InputNumber
                  inputId="areaSqm"
                  value={areaSqm}
                  onChange={handleSqMeterChange}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  className="cost_input custom w-100"
                  placeholder="0"
                />
                {areaSqmEr && (
                  <small className="form_error_msg">{areaSqmEr}</small>
                )}
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label
                  htmlFor="areaSqft"
                  className="cost_title custom is_required"
                >
                  Area (sqft)
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <InputNumber
                  inputId="areaSqft"
                  value={areaSqft}
                  onChange={handleSqFeetChange}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  className="cost_input custom w-100"
                  placeholder="0"
                />
                {areaSqftEr && (
                  <small className="form_error_msg">{areaSqftEr}</small>
                )}
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label
                  htmlFor="pricePerSqm"
                  className="cost_title custom is_required"
                >
                  Price per sqm
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <InputNumber
                  inputId="pricePerSqm"
                  value={pricePerSqm}
                  onChange={handleSqMeterPriceChange}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="currency"
                  currency="GBP"
                  locale="en-UK"
                  className="cost_input custom w-100"
                  placeholder="0"
                />
                {pricePerSqmEr && (
                  <small className="form_error_msg">{pricePerSqmEr}</small>
                )}
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label
                  htmlFor="pricePerSqft"
                  className="cost_title custom is_required"
                >
                  Price per sqft
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <InputNumber
                  inputId="pricePerSqft"
                  value={pricePerSqft}
                  onChange={handleSqFeetPriceChange}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="currency"
                  currency="GBP"
                  locale="en-UK"
                  className="cost_input custom w-100"
                  placeholder="0"
                />
                {pricePerSqftEr && (
                  <small className="form_error_msg">{pricePerSqftEr}</small>
                )}
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label htmlFor="totalPrice" className="cost_title custom">
                  Total price
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <InputNumber
                  inputId="totalPrice"
                  value={totalPrice}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="currency"
                  currency="GBP"
                  locale="en-UK"
                  readOnly
                  className="cost_input custom w-100"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label htmlFor="numberOfBedroom" className="cost_title custom">
                  No. of Bedroom(s)
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <InputNumber
                  inputId="numberOfBedroom"
                  value={numberOfBedroom}
                  onValueChange={(e: InputNumberValueChangeEvent) => setNumberOfBedroom(e.value || 0)}
                  className="cost_input custom w-100"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label htmlFor="numberOfBathroom" className="cost_title custom">
                  No. of Bathroom(s)
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <InputNumber
                  inputId="numberOfBathroom"
                  value={numberOfBathroom}
                  onValueChange={(e: InputNumberValueChangeEvent) => setNumberOfBathroom(e.value || 0)}
                  className="cost_input custom w-100"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label
                  htmlFor="numberOfShowerroom"
                  className="cost_title custom"
                >
                  No. of Showerroom(s)
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <InputNumber
                  inputId="numberOfShowerroom"
                  value={numberOfShowerroom}
                  onValueChange={(e: InputNumberValueChangeEvent) => setNumberOfShowerroom(e.value || 0)}
                  className="cost_input custom w-100"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label htmlFor="garden" className="cost_title custom">
                  Garden
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <SelectButton
                  id="garden"
                  value={garden}
                  onChange={(e: SelectButtonChangeEvent) => setGarden(e.value)}
                  options={options}
                  className="option_switch"
                />
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label htmlFor="balcony" className="cost_title custom">
                  Balcony
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <SelectButton
                  id="balcony"
                  value={balcony}
                  onChange={(e: SelectButtonChangeEvent) => setBalcony(e.value)}
                  options={options}
                  className="option_switch"
                />
              </div>
            </div>

            <div className="row mb-sm-2 mb-1">
              <div className="col-7 col-sm-8 pe-sm-2 pe-1">
                <label htmlFor="flatLocation" className="cost_title custom">
                  Location
                </label>
              </div>
              <div className="col-5 col-sm-4 ps-sm-2 ps-1">
                <InputText
                  id="flatLocation"
                  value={flatLocation}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFlatLocation(e.target.value)}
                  className="cost_input custom text_input w-100"
                  placeholder="Enter location"
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </React.Fragment>
  );
};

export default GDVCostDetail;
